import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminApi } from '@formosa/shared/api/admin';
import { ApiError } from '@formosa/shared/api/createClient';
import { useGoogleVerifyConfig, validateGoogleCodeInput } from '../../hooks/useGoogleVerifyConfig';

export default function BalanceAdjustPage() {
  const [searchParams] = useSearchParams();
  const { config } = useGoogleVerifyConfig();
  const [direction, setDirection] = useState('CREDIT');
  const [memberId, setMemberId] = useState('');
  const [amount, setAmount] = useState('');
  const [fee, setFee] = useState('');
  const [remark, setRemark] = useState('');
  const [currencyName, setCurrencyName] = useState('');
  const [googleCode, setGoogleCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const showGoogleField = !config.skip;

  useEffect(() => {
    const id = searchParams.get('memberId');
    if (id) {
      setMemberId(id);
    }
  }, [searchParams]);

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');
    setSuccess('');

    const googleError = config.skip ? '' : validateGoogleCodeInput(config, googleCode);
    if (googleError) {
      setError(googleError);
      return;
    }

    setLoading(true);

    const payload = {
      direction,
      memberId: Number(memberId),
      amount: Number(amount),
      remark: remark || undefined,
      currencyName: currencyName || undefined,
    };

    if (direction === 'DEBIT' && fee !== '') {
      payload.fee = Number(fee);
    }
    const code = googleCode.trim();
    if (code) {
      payload.googleCode = code;
    }

    try {
      const result = await adminApi.adjustBalance(payload);
      setSuccess(
        `操作成功，異動後餘額：${result?.balanceAfter ?? result?.balance ?? '—'}`
      );
      setAmount('');
      setFee('');
      setRemark('');
      setGoogleCode('');
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : '操作失敗，請稍後再試';
      setError(message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="page-card page-narrow">
      <h2>系統上下分</h2>
      <p className="page-desc">
        上分（CREDIT）增加餘額；下分（DEBIT）扣除餘額，可選填手續費。
      </p>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-field">
          <label htmlFor="direction">方向</label>
          <select
            id="direction"
            value={direction}
            onChange={(e) => setDirection(e.target.value)}
          >
            <option value="CREDIT">上分（加帳）</option>
            <option value="DEBIT">下分（減帳）</option>
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="memberId">會員 ID</label>
          <input
            id="memberId"
            type="number"
            min="1"
            value={memberId}
            onChange={(e) => setMemberId(e.target.value)}
            required
          />
        </div>

        <div className="form-field">
          <label htmlFor="amount">金額</label>
          <input
            id="amount"
            type="number"
            min="0.01"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
          />
        </div>

        {direction === 'DEBIT' && (
          <div className="form-field">
            <label htmlFor="fee">手續費（選填）</label>
            <input
              id="fee"
              type="number"
              min="0"
              step="0.01"
              value={fee}
              onChange={(e) => setFee(e.target.value)}
            />
          </div>
        )}

        <div className="form-field">
          <label htmlFor="currencyName">幣種（選填，預設平臺幣）</label>
          <input
            id="currencyName"
            value={currencyName}
            onChange={(e) => setCurrencyName(e.target.value)}
            placeholder="USDT"
          />
        </div>

        <div className="form-field">
          <label htmlFor="remark">備註</label>
          <input
            id="remark"
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            placeholder="操作說明"
          />
        </div>

        {showGoogleField && (
          <div className="form-field">
            <label htmlFor="balanceGoogleCode">
              {!config.skip && <span className="required-mark">*</span>} 谷歌驗證碼
            </label>
            <input
              id="balanceGoogleCode"
              type="text"
              inputMode="numeric"
              value={googleCode}
              onChange={(e) => setGoogleCode(e.target.value)}
              placeholder={config.skip ? '跳過開啟時可不填' : '請輸入 6 位驗證碼'}
            />
          </div>
        )}

        <button
          type="submit"
          className="btn btn-primary btn-block"
          disabled={loading}
        >
          {loading ? '提交中…' : '確認提交'}
        </button>
      </form>
    </div>
  );
}
